import { type FetchInput, type FetchService } from './fetch';
import type { ServiceDescriptor } from '@conduit-client/utils';

// AbortError class that matches the standard AbortError
class AbortError extends Error {
    name = 'AbortError';
    constructor(message = 'This operation was aborted') {
        super(message);
    }
}

export type MockResponse = {
    status?: number;
    statusText?: string;
    ok?: boolean;
    body?: unknown;
    delay?: number; // Delay in milliseconds to simulate network latency
};

/**
 * A response queued onto the mock service: either a `MockResponse` (resolved
 * into a synthetic `Response`) or an `Error` (rejected from the call).
 */
export type QueuedResponse = MockResponse | Error;

export type MockFetchService = {
    readonly requests: Array<Parameters<FetchService>>;
    readonly availableResponses: number;
    // Accepts a single queued response or an array of them; the implementation
    // concats either form onto the pending queue (Array.concat flattens an
    // array argument and appends a scalar one).
    queueResponses: (newResponses: QueuedResponse | Array<QueuedResponse>) => void;
    reset: () => void;
    readonly responsesUsed: number;
} & FetchService;

export function buildMockFetchService(
    initialResponses: Array<QueuedResponse> = []
): ServiceDescriptor<MockFetchService, 'fetch', '1.0'> {
    let responses: Array<QueuedResponse> = [...initialResponses];

    const networkAdapter = (...args: Parameters<FetchService>) => {
        // Capture the full fetch parameters for more realistic testing
        const [_, fetchOptions = {}] = args;

        networkAdapter.requests.push(args);

        // Immediate abort detection - check if signal is already aborted
        if (fetchOptions.signal?.aborted) {
            return Promise.reject(new AbortError());
        }

        const result = responses.shift();
        if (result === undefined) {
            throw new Error('No more mock responses queued');
        }
        networkAdapter.availableResponses = responses.length;
        networkAdapter.responsesUsed++;

        if (result instanceof Error) {
            return Promise.reject(result);
        }

        // Simulate request delay if specified
        const delay = (result as MockResponse).delay || 0;

        // Create a promise that handles both delay simulation and runtime abort
        return new Promise<Response>((resolve, reject) => {
            let abortHandler: (() => void) | null = null;

            // Set up abort listener for runtime abort detection
            if (fetchOptions.signal) {
                abortHandler = () => {
                    reject(new AbortError());
                };
                fetchOptions.signal.addEventListener('abort', abortHandler);
            }

            const completeRequest = () => {
                // Clean up abort listener
                if (abortHandler && fetchOptions.signal) {
                    fetchOptions.signal.removeEventListener('abort', abortHandler);
                }

                // Check one more time if aborted during delay
                if (fetchOptions.signal?.aborted) {
                    reject(new AbortError());
                    return;
                }

                // Return successful response
                resolve({
                    ok: result.ok !== undefined ? result.ok : true,
                    statusText: result.statusText !== undefined ? result.statusText : 'ok',
                    status: result.status !== undefined ? result.status : 200,
                    json: () => Promise.resolve(result.body),
                } as Response);
            };

            if (delay > 0) {
                setTimeout(completeRequest, delay);
            } else {
                // Always use async behavior to maintain existing test expectations
                // Even without delay, tests expect async behavior for wire adapters
                setTimeout(completeRequest, 0);
            }
        });
    };

    networkAdapter.requests = [] as Array<Parameters<FetchService>>;
    networkAdapter.availableResponses = responses.length;

    networkAdapter.queueResponses = (newResponses: QueuedResponse | Array<QueuedResponse>) => {
        responses = responses.concat(newResponses);
        networkAdapter.availableResponses = responses.length;
    };

    networkAdapter.fetch = (args: FetchInput) => networkAdapter(args);

    networkAdapter.reset = () => {
        networkAdapter.requests = [];
        responses = [];
        networkAdapter.availableResponses = 0;
        networkAdapter.responsesUsed = 0;
    };

    networkAdapter.responsesUsed = 0;
    return {
        type: 'fetch',
        version: '1.0',
        service: networkAdapter as MockFetchService,
    };
}
